import { EventEmitter, Injectable, Output, signal } from '@angular/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  ObjectKey,
  PersistMode,
  PersistObjectsModel,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
  DataService as CoreDataService,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import { MeasureTask, Project, State, SubProject } from '@barthauer/custom/xmt-models';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { ObjectKeys } from './data.service';
import { AbcCore } from '@barthauer/abc/core';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  @Output() selectItem = new EventEmitter();

  public measureName = signal<string>('');
  public subProjectName = signal<string>('');
  public projectName = signal<string>('');

  public reportIndex: number;
  public measureIds = [];
  public showMeasureIcon: boolean = false;
  public currentUserName: string;
  private specificInfo: SpecificInfo;
  public readonly CONFIG_PROPERTIES = [
    'Id',
    'ParentId',
    'SysTimeInsert',
    'SysTimeUpdate',
    'SysUserNameInsert',
    'SysDateUpdate',
    'SysUserNameUpdate',
    'SeqNr',
    'ObjectType',
  ];
  public res = ResourceManager.getResources('ubt-geods');
  public subProjectRequestColumns = new QueryObjectsModel();
  public subProjects: SubProject[] = [];
  public currentSubProjectIndex: number = 0;
  public currentMeasureProgressIndex = signal(-1);
  public subProjectIds: string[] = [];
  public treeViewSwitchToCompany = new BehaviorSubject<boolean>(false);
  public treeViewSwitchToCompany$ = this.treeViewSwitchToCompany.asObservable();
  public treeViewSwitchToProject = new BehaviorSubject<boolean>(false);
  public treeViewSwitchToProject$ = this.treeViewSwitchToProject.asObservable();
  public loggedInUserInformations = new ReplaySubject<{
    fullName: string;
    id: string;
  }>();
  public loggedInUserInformations$ = this.loggedInUserInformations.asObservable();
  public isCompanySelected = signal(false);
  public projectList: Project[] = [];
  private measureTask = new MeasureTask(null);
  private loaderSubject: Subject<boolean> = new Subject();
  public loader$: Observable<boolean> = this.loaderSubject.asObservable();
  public loadMeasureDetails: boolean = false;

  constructor(
    private configService: AbcConfigService,
    private coreDataService: CoreDataService,
  ) {
    const currentConfiguration = configService.getConfiguration();
    this.specificInfo = currentConfiguration.SpecificInfo;
  }

  public showLoader(isLoaderVisible: boolean): void {
    this.loaderSubject.next(isLoaderVisible);
  }
  get locale(): string {
    const lang = sessionStorage.getItem('currentUserLanguage');
    return lang === 'de' ? 'de-DE' : 'en-US';
  }
  public sanitizeContent(content: string, isPastedContent?: boolean): string {
    if (content) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      if (isPastedContent) {
        const strongElements = doc.querySelectorAll('strong');
        strongElements.forEach((element) => {
          const parent = element.parentNode;
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        });
      }

      const elementsWithStyles = doc.querySelectorAll('*[style]');
      elementsWithStyles.forEach((element) => {
        let style = element.getAttribute('style') || '';

        style = style.replace(/font-size:[^;]+;?/g, '').replace(/font-family:[^;]+;?/g, '');

        if (isPastedContent) {
          style = style.replace(/font-weight:[^;]+;?/g, '').replace(/color:[^;]+;?/g, '');
        }

        const newStyle = `${style} font-size: 14px; font-family: "Helvetica Neue", "Segoe UI", helvetica, verdana, sans-serif;`;
        element.setAttribute('style', newStyle);
      });

      return doc.body.innerHTML;
    }
    return content;
  }

  public getSpecificInfo(): SpecificInfo {
    return this.specificInfo;
  }
  public loadNotOnlyMeasureDetails(check: boolean) {
    return (this.loadMeasureDetails = check);
  }

  public selectTreeItem = (selectedNode: number) => {
    this.selectItem.emit(selectedNode);
  };

  public switchTreeViewToCompany(data: boolean) {
    localStorage.setItem('switchToCompany', data.toString());
    this.treeViewSwitchToCompany.next(data);
  }

  public switchTreeViewToProject(data: boolean) {
    localStorage.setItem('switchToProject', data.toString());
    this.treeViewSwitchToProject.next(data);
  }

  public getMeasureTask(): MeasureTask {
    return this.measureTask;
  }

  public setMeasureTask(measureTask: MeasureTask): void {
    this.measureTask = measureTask;
  }

  public persistMultipleObjects(
    objectsOrKey: { objectKey: ObjectKeys; persistMode: PersistMode; data: any | any[] }[] | ObjectKeys,
    persistMode?: PersistMode,
    data?: any[],
  ): Observable<any> {
    const buildObjectData = (object: any, persistMode: PersistMode, objectKey: ObjectKeys): TargetObjectData => {
      const objectData = new TargetObjectData();
      objectData.Mode = persistMode;
      objectData.ObjectKey = new ObjectKey();
      objectData.ObjectKey.ObjectType = objectKey;

      const queryColumns: TargetColumnValue[] = [];

      if (persistMode === PersistMode.Delete) {
        objectData.ObjectKey.Id = object.Id;
      } else {
        Object.entries(object).forEach(([key, value]) => {
          if (key === 'Id') objectData.ObjectKey.Id = value as string;
          queryColumns.push({ Name: key, Value: value });
        });
        objectData.TargetColumns = queryColumns;
      }

      return objectData;
    };

    const query: PersistObjectsModel = new PersistObjectsModel();
    const objectsPersistQuery: TargetObjectData[][] = [[]];

    if (Array.isArray(objectsOrKey)) {
      objectsOrKey.forEach(({ objectKey, persistMode, data }) => {
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((object) => {
            objectsPersistQuery[0].push(buildObjectData(object, persistMode, objectKey));
          });
        } else if (data) {
          objectsPersistQuery[0].push(buildObjectData(data, persistMode, objectKey));
        }
      });
    } else {
      data?.forEach((object) => {
        objectsPersistQuery[0].push(buildObjectData(object, persistMode, objectsOrKey));
      });
    }

    query.Objects = objectsPersistQuery;
    return this.coreDataService.executePersistObjectsQuery(query);
  }

  /**
   * Prepares a Date object based on the provided date.
   * If no date is provided, the current date and time will be used.
   *
   * @param date - The date to be prepared. If not provided, defaults to the current date and time.
   * @returns A new Date object representing the specified date in UTC format.
   */

  public getCssClassByStatus(status: State): string {
    switch (status) {
      case State.Open:
        return 'notif-bubble-open';
      case State.Permanent:
        return 'notif-bubble-permanent';
      case State.BeforeDueDate:
        return 'notif-bubble-beforeDueDate';
      case State.AfterDueDate:
        return 'notif-bubble-afterDueDate';
      case State.OneTime:
        return 'notif-bubble-done';
      case State.Repetitive:
        return 'notif-bubble-repetitive';
      case State.Noted:
        return 'notif-bubble-noted';
      case State.Done:
        return 'notif-bubble-finished';
    }
  }
}

export interface SpecificInfo {
  logoIcon: string;
  logoMiniatureIcon: string;
}
